import { Button, Checkbox, Group } from '@mantine/core';
import { IconFileTypeXls } from '@tabler/icons-react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TravelOverlapCardFrame } from '@/module/Travel/Request/components/TravelOverlapButton';

import { Browse, usePagination } from '@/core/components';

import { FilterBox, SearchFilter, useAppNotification, useSearch, useSortDirection } from '@feature/utils';

import { TravelRegistryCard } from './components/TravelRegistryCard';
import { getTravelRegistryPagedOptions, useTravelRegistryExport } from './model/TravelRegistryQuery';
import { DEFAULT_TRAVEL_REGISTRY_LANG } from './model/TravelRegistryTypes';
import { useTravelRequestFilters } from './useTravelRequestFilters';

export function TravelRegistryBrowse() {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { search, searchProps } = useSearch();
  const [full, setFull] = useState(false);
  const { pagination, useTotalElements, resetPagination, ...paginationProps } = usePagination({ search });
  const { filter, fields } = useTravelRequestFilters({
    initialYear: null,
    showStatuses: true,
    restrictDeanFaculty: true,
    onChange: resetPagination,
  });
  const exportRegistry = useTravelRegistryExport();

  const { data } = useSuspenseQuery(
    getTravelRegistryPagedOptions({
      ...pagination,
      ...filter,
      sortDirection,
      search,
    }),
  );
  useTotalElements(data);

  // No language picker in the app yet, so the export follows the Excel export precedent and stays in ro.
  const onExport = () =>
    exportRegistry.mutate(
      { ...filter, search, lang: DEFAULT_TRAVEL_REGISTRY_LANG, full },
      { onError: notification.error },
    );

  return (
    <Browse
      actions={
        <Group gap="sm">
          <Checkbox
            size="xs"
            label={t('travel.registry.exportFull')}
            checked={full}
            onChange={event => setFull(event.currentTarget.checked)}
          />
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconFileTypeXls size={14} />}
            loading={exportRegistry.isPending}
            onClick={onExport}
          >
            {t('travel.registry.export')}
          </Button>
        </Group>
      }
      title={t('travel.registry.title')}
      pagination={paginationProps}
      filters={
        <FilterBox>
          <SearchFilter
            label={t('label.search')}
            description={t('travel.registry.searchBy')}
            {...searchProps}
          />
          {fields}
        </FilterBox>
      }
      sortDirection={sortDirection}
      sortedBy={t('travel.registry.sortedBy')}
      toggleSortDirection={toggleSortDirection}
      data={data.list}
      renderItem={row => (
        <TravelOverlapCardFrame
          key={row.id}
          requestId={row.id}
          overlapCount={row.overlapCount}
        >
          <TravelRegistryCard row={row} />
        </TravelOverlapCardFrame>
      )}
    />
  );
}
